import React, { useMemo } from 'react';
import ChartCard, {
    ChartCardContent as CardContent,
    ChartCardHeader as CardHeader,
    ChartCardTitle as CardTitle,
} from '../charts/ChartCard';
import EChart from '../charts/EChart';
import { useChartPalette } from '../charts/useChartMode';
import { ChartPalette, sentimentSliceColor } from '../charts/chartTheme';
import { donutOption } from '../charts/options';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

// ── Types ──────────────────────────────────────────────────────────

export interface ContentInsight {
    type: 'themes' | 'trending' | 'sentiment' | 'quality';
    title: string;
    items: Array<{ label: string; value: number; percentage?: number }>;
}

export interface Recommendation {
    id: string;
    type: 'geographic_expansion' | 'format_opportunity' | 'content_alignment' | 'risk_flag' | 'optimization';
    title: string;
    description: string;
    dataCitation: string;
    dismissed?: boolean;
}

interface AIInsightsPanelProps {
    insights: ContentInsight[];
    recommendations: Recommendation[];
    onDismissRecommendation: (id: string) => void;
    onGenerateRecommendations: () => void;
    loading?: boolean;
    recommendationsLoading?: boolean;
}

// ── Main Component ─────────────────────────────────────────────────

const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
    insights,
    recommendations,
    onDismissRecommendation,
    onGenerateRecommendations,
    loading = false,
    recommendationsLoading = false,
}) => {
    const visibleRecs = recommendations.filter((r) => !r.dismissed);

    return (
        <ChartCard>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle>Content Insights &amp; AI Recommendations</CardTitle>
                    <Button size="sm" onClick={onGenerateRecommendations} disabled={recommendationsLoading}>
                        {recommendationsLoading ? 'Generating…' : 'Get Recommendations'}
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <InsightsBody insights={insights} loading={loading} />
                <RecommendationsBody
                    recommendations={visibleRecs}
                    loading={recommendationsLoading}
                    onDismiss={onDismissRecommendation}
                />
            </CardContent>
        </ChartCard>
    );
};

// ── Insights Section ───────────────────────────────────────────────

function InsightsBody({ insights, loading }: { insights: ContentInsight[]; loading: boolean }) {
    if (loading) return <InsightsLoadingSkeleton />;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {insights.map((insight) => (
                <InsightCard key={insight.type} insight={insight} />
            ))}
        </div>
    );
}

function InsightsLoadingSkeleton() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse space-y-3 p-4 border border-line rounded-xl">
                    <div className="h-4 bg-line rounded w-32" />
                    <div className="h-24 bg-canvas rounded" />
                </div>
            ))}
        </div>
    );
}

function InsightCard({ insight }: { insight: ContentInsight }) {
    return (
        <div className="bg-canvas rounded-xl p-3">
            <h4 className="text-xs font-bold text-accent-active mb-2">{insight.title}</h4>
            <InsightBody insight={insight} />
        </div>
    );
}

function InsightBody({ insight }: { insight: ContentInsight }) {
    if (insight.type === 'sentiment') return <SentimentPieChart items={insight.items} />;
    if (insight.type === 'quality') return <QualityBars items={insight.items} />;
    return <ItemList items={insight.items} />;
}

// ── Insight Visualisations ─────────────────────────────────────────

// Content quality is a verdict, so it wears the reserved status colours (and
// always carries a text label beside the swatch — colour never carries the
// meaning alone). Sentiment is handled separately by `sentimentSliceColor`:
// it wears the teal/pink categorical slots so both sentiment donuts on the
// board read as one system rather than as green/red alarms.
function verdictColor(label: string, palette: ChartPalette): string {
    if (label === 'High') return palette.status.good;
    if (label === 'Medium') return palette.status.warning;
    if (label === 'Low') return palette.status.critical;
    return palette.status.neutral;
}

function SentimentPieChart({ items }: { items: ContentInsight['items'] }) {
    const palette = useChartPalette();
    const lead = items.find((item) => item.label === 'Positive') ?? items[0];

    const option = useMemo(
        () =>
            donutOption({
                palette,
                slices: items.map((item) => ({
                    name: item.label,
                    value: item.value,
                    color: sentimentSliceColor(item.label, palette),
                })),
                centerValue: lead ? `${Math.round(lead.percentage ?? 0)}%` : undefined,
                centerLabel: lead?.label,
                radius: ['58%', '86%'],
                height: 120,
            }),
        [items, lead, palette],
    );

    return (
        <div className="flex items-center gap-4">
            <div className="w-[120px] shrink-0">
                <EChart option={option} height={120} ariaLabel="Sentiment split" />
            </div>
            <SentimentLegend items={items} palette={palette} />
        </div>
    );
}

function SentimentLegend({ items, palette }: { items: ContentInsight['items']; palette: ChartPalette }) {
    return (
        <ul className="space-y-1 text-xs text-ink-muted">
            {items.map((item) => (
                <li key={item.label} className="flex items-center gap-2">
                    <span
                        data-testid="sentiment-legend-dot"
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: sentimentSliceColor(item.label, palette) }}
                    />
                    {item.label} — {item.percentage?.toFixed(0)}%
                </li>
            ))}
        </ul>
    );
}

function QualityBars({ items }: { items: ContentInsight['items'] }) {
    return (
        <div className="space-y-2">
            {items.map((item) => (
                <QualityBarRow key={item.label} item={item} />
            ))}
        </div>
    );
}

function QualityBarRow({ item }: { item: ContentInsight['items'][number] }) {
    const palette = useChartPalette();
    return (
        <div>
            <div className="flex justify-between text-xs text-ink-muted mb-0.5">
                <span>{item.label}</span>
                <span className="tabular-nums">{item.percentage?.toFixed(0)}%</span>
            </div>
            <div className="h-2 bg-canvas rounded-full overflow-hidden">
                <div
                    data-testid="quality-bar-fill"
                    className="h-full rounded-full transition-all"
                    style={{
                        width: `${item.percentage ?? 0}%`,
                        backgroundColor: verdictColor(item.label, palette),
                    }}
                />
            </div>
        </div>
    );
}

function ItemList({ items }: { items: ContentInsight['items'] }) {
    return (
        <ul className="space-y-1.5">
            {items.map((item) => (
                <li key={item.label} className="flex justify-between text-sm">
                    <span className="text-ink">{item.label}</span>
                    <span className="text-ink-muted">
                        {item.value.toLocaleString()}
                        {item.percentage != null && (
                            <span className="ml-1 text-xs text-ink-muted">({item.percentage.toFixed(1)}%)</span>
                        )}
                    </span>
                </li>
            ))}
        </ul>
    );
}

// ── Recommendations Section ────────────────────────────────────────

function RecommendationsBody({
    recommendations,
    loading,
    onDismiss,
}: {
    recommendations: Recommendation[];
    loading: boolean;
    onDismiss: (id: string) => void;
}) {
    if (loading) return <RecommendationsLoadingSkeleton />;
    if (recommendations.length === 0) {
        return (
            <p className="text-sm text-ink-muted">
                Click "Get Recommendations" to generate AI-powered suggestions.
            </p>
        );
    }
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {recommendations.map((rec) => (
                <RecommendationCard key={rec.id} rec={rec} onDismiss={onDismiss} />
            ))}
        </div>
    );
}

function RecommendationsLoadingSkeleton() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {[...Array(2)].map((_, i) => (
                <div key={i} className="animate-pulse bg-canvas rounded-xl p-3 space-y-2">
                    <div className="h-4 bg-line rounded w-48" />
                    <div className="h-3 bg-line rounded w-full" />
                    <div className="h-3 bg-line rounded w-3/4" />
                </div>
            ))}
        </div>
    );
}

// Fixed token-pair mapping — colour always carries the same meaning as
// elsewhere in the app: geo/format opportunities are interactive-accent,
// content alignment is safe, risk flags are risk, optimizations are warn.
const RECOMMENDATION_STYLES: Record<Recommendation['type'], { variant: 'default' | 'safe' | 'warn' | 'risk'; label: string }> = {
    geographic_expansion: { variant: 'default', label: 'Geo Expansion' },
    format_opportunity: { variant: 'default', label: 'Format Opportunity' },
    content_alignment: { variant: 'safe', label: 'Content Alignment' },
    risk_flag: { variant: 'risk', label: 'Risk Flag' },
    optimization: { variant: 'warn', label: 'Optimization' },
};

function RecommendationCard({
    rec,
    onDismiss,
}: {
    rec: Recommendation;
    onDismiss: (id: string) => void;
}) {
    const style = RECOMMENDATION_STYLES[rec.type];

    return (
        <div className="bg-canvas rounded-xl p-3 flex gap-3 relative">
            <RecommendationContent rec={rec} badgeStyle={style} />
            <button
                onClick={() => onDismiss(rec.id)}
                className="absolute right-2.5 top-2 shrink-0 text-ink-muted hover:text-ink font-bold"
                aria-label="Dismiss"
            >
                ×
            </button>
        </div>
    );
}

function RecommendationContent({
    rec,
    badgeStyle,
}: {
    rec: Recommendation;
    badgeStyle: { variant: 'default' | 'safe' | 'warn' | 'risk'; label: string };
}) {
    return (
        <div className="flex-1 min-w-0">
            <Badge variant={badgeStyle.variant} className="mb-1">
                {badgeStyle.label}
            </Badge>
            <p className="font-semibold text-sm text-ink">{rec.title}</p>
            <p className="text-sm text-ink-muted">{rec.description}</p>
            <p className="text-xs text-ink-muted opacity-70 mt-1">{rec.dataCitation}</p>
        </div>
    );
}

export default AIInsightsPanel;
